import {Injectable} from '@angular/core';
import {SharedFacadeService} from '@shared/shared-facade.service';
import {select, Store} from '@ngrx/store';
import {
  getEvaluationsAveragesEntities,
  getEvaluationScores,
  getScoreBookEvaluationComments,
  getScoreBookEvaluations,
  getScoreBookEvaluationsById,
  StoreState
} from '@redux/reducers';
import {combineLatest, concatAll, Observable, of} from 'rxjs';
import {StaffMember} from '@model/staffMember';
import {Profile} from '@model/profile';
import {School} from '@model/school';
import {map, mergeAll, switchMap, take} from 'rxjs/operators';
import {
  scoreBookCompetenceDeleted,
  scoreBookCompetenceSaved
} from '@redux/actions/score-book-competence.action';
import {Competence} from '@model/competence';
import {SchoolReportType} from '@model/school-report-type';
import {ScoreBookService} from '@services/score-book.service';
import {Evaluation} from '@model/evaluation';
import {
  scoreBookEvaluationDeleted,
  scoreBookEvaluationLoaded,
  scoreBookEvaluationSaved,
  scoreBookEvaluationsReset
} from '@redux/actions/score-book-evaluation.action';
import {StudentInAttribution} from '@model/student-in-attribution';
import {EvaluationScore} from '@model/evaluation-score';
import {
  scoreBookEvaluationCommentSaved,
  scoreBookEvaluationCommentsLoaded,
  scoreBookEvaluationCommentsReset,
  scoreBookEvaluationScoreSaved,
  scoreBookEvaluationScoresLoaded,
  scoreBookEvaluationScoresReset
} from '@redux/actions/score-book-evaluation-scores.action';
import {scoreBookEvaluationsAveragesLoaded} from '@redux/actions/score-book-evaluations-averages.action';
import {EvaluationsAverage} from '@model/evaluations-average';
import {SchoolReportScore} from '@model/school-report-score';
import {SchoolReportsService} from '@services/school-reports.service';
import {AverageComputationType} from '@model/average-computation-type';
import {EvaluationComment} from '@model/evaluation-comment';
import {MatSnackBar} from '@angular/material/snack-bar';
import {saveAs} from 'file-saver';
import {SchoolSettingKey} from '@model/school-setting-key';
import {AttributionSummary} from "@model/attribution-summary";
import {AverageComputationEvaluationType} from "@model/average-computation-evaluation-type";

@Injectable()
export class ScoreBookFacadeService {
  staffMembers$: Observable<StaffMember[]>;
  profile$: Observable<Profile>;
  attributionsActivesForTeacher$: Observable<AttributionSummary[]>;
  private _school$: Observable<School>;
  activeSchoolYear$: Observable<number>;
  competences$: Observable<Competence[]>;
  schoolReportTypesByAttributionId$: Observable<{ [attributionId: number]: SchoolReportType }>;
  evaluations$: Observable<Evaluation[]>;
  evaluationsById$: Observable<{ [evaluationId: number]: Evaluation }>;
  studentsByAttribution$: Observable<{ [id: number]: StudentInAttribution[]; }>;
  evaluationScores$: Observable<EvaluationScore[]>;
  evaluationComments$: Observable<EvaluationComment[]>;
  evaluationsAverages$: Observable<{ [studentId: number]: EvaluationsAverage }>;
  schoolReportTypesByClassId$: Observable<{ [classId: number]: SchoolReportType }>;
  isPendingRequest$: Observable<boolean>;
  isAverageWithAllEvalEnabled$: Observable<boolean>;
  isElementarySchool$: Observable<boolean>;

  constructor(private _store: Store<StoreState>,
              private _scoreBookService: ScoreBookService,
              private _schoolReportsService: SchoolReportsService,
              private _sharedFacade: SharedFacadeService,
              private _snackBar: MatSnackBar) {
    this._school$ = this._sharedFacade.school$;
    this.activeSchoolYear$ = this._sharedFacade.activeSchoolYear$;
    this.staffMembers$ = this._sharedFacade.staffMembers$;
    this.profile$ = this._sharedFacade.profile$;
    this.attributionsActivesForTeacher$ = this._sharedFacade.attributionsActivesForTeacher$;
    this.schoolReportTypesByAttributionId$ = this._sharedFacade.schoolReportTypesByAttributionId$;
    this.competences$ = this._sharedFacade.competences$;
    this.evaluations$ = this._store.pipe(
      select(getScoreBookEvaluations)
    );
    this.evaluationsById$ = this._store.pipe(
      select(getScoreBookEvaluationsById)
    );
    this.studentsByAttribution$ = this._sharedFacade.studentsByAttribution$;
    this.evaluationScores$ = this._store.pipe(
      select(getEvaluationScores)
    );
    this.evaluationsAverages$ = this._store.pipe(
      select(getEvaluationsAveragesEntities)
    );
    this.evaluationComments$ = this._store.pipe(
      select(getScoreBookEvaluationComments)
    );
    this.schoolReportTypesByClassId$ = this._sharedFacade.schoolReportTypesByClassId$;
    this.isPendingRequest$ = this._sharedFacade.isPendingRequest$;
    this.isAverageWithAllEvalEnabled$ = this._sharedFacade.schoolSettings$.pipe(
      map(schoolSettings => schoolSettings.find(schoolSetting => schoolSetting.key === SchoolSettingKey.AVERAGE_WITH_ALL_EVALUATIONS_FOR_SCORE_BOOK)?.enabled)
    );
    this.isElementarySchool$ = this._sharedFacade.elementarySchool$;
  }

  loadStaffMembers() {
    this._sharedFacade.loadStaffMembers();
  }

  loadAttributionsActivesForConnectedTeacher() {
    this._sharedFacade.loadAttributionsActivesForConnectedTeacher();
  }

  loadAttributionsActivesForTeacherAndYear(staffMemberId: number, schoolYear: number) {
    this._sharedFacade.loadAttributionsActivesForTeacher(staffMemberId, schoolYear);
  }

  loadSchoolReportTypeAndClassAssignments(schoolYear?: number) {
    this._sharedFacade.loadSchoolReportTypes(schoolYear);
    this._sharedFacade.loadSchoolReportTypeToClassAssignments(schoolYear);
  }

  loadStudentsByAttribution(attributionId: number) {
    this._sharedFacade.loadStudentsByAttribution(attributionId);
  }

  loadCompetences(attributionId: number) {
    this._sharedFacade.loadCompetences(attributionId);
  }

  copyCompetences(attributionIdFrom: number, attributionIdTo: number) {
    this._school$.pipe(
      take(1),
      switchMap(school => combineLatest([of(school.id),this._scoreBookService.loadCompetences(school.id, attributionIdFrom)])),
      switchMap(([schoolId, competences]) => {
        if (competences.length === 0) {
          throw new Error("NoDataToCopy");
        }
        return competences.map(competence =>
          this._scoreBookService.saveCompetence(
            schoolId, {
              ...competence,
              id: undefined,
              attributionId: attributionIdTo,
              weights: []
            }))}),
      concatAll()
    ).subscribe({
      next: (competenceSaved) => {
        this._store.dispatch(scoreBookCompetenceSaved({competence: competenceSaved}));
      },
      error: (error) => {
        if (error.message === "NoDataToCopy") {
          this._snackBar.open("Aucune compétence à copier", null, {duration: 2000});
        } else {
          throw new Error(error);
        }
      },
      complete: () => this._snackBar.open("Compétences recopiées avec succès", null, {duration: 2000})
    });
  }

  deleteCompetence(competence: Competence) {
    this._school$.pipe(
      take(1),
      switchMap(school => this._scoreBookService.deleteCompetence(school.id, competence))
    ).subscribe(() => this._store.dispatch(scoreBookCompetenceDeleted({competenceId: competence.id})));
  }

  saveCompetence(competence: Competence) {
    this._school$.pipe(
      take(1),
      switchMap(school => this._scoreBookService.saveCompetence(school.id, competence))
    ).subscribe(competenceSaved => this._store.dispatch(scoreBookCompetenceSaved({competence: competenceSaved})));
  }

  loadEvaluations(attributionId: number, periodCode?: string) {
    this._school$.pipe(
      take(1),
      switchMap(school => this._scoreBookService.loadEvaluations(school.id, attributionId, periodCode))
    ).subscribe(result => this._store.dispatch(scoreBookEvaluationLoaded({evaluations: result})));
  }

  getEvaluation(evaluationId: number) {
    this._school$.pipe(
      take(1),
      switchMap(school => this._scoreBookService.getEvaluation(school.id, evaluationId))
    ).subscribe(result => this._store.dispatch(scoreBookEvaluationLoaded({evaluations: [result]})));
  }

  saveEvaluation(evaluation: Evaluation) {
    this._school$.pipe(
      take(1),
      switchMap(school => this._scoreBookService.saveEvaluation(school.id, evaluation))
    ).subscribe(evaluationSaved => this._store.dispatch(scoreBookEvaluationSaved({evaluation: evaluationSaved})));
  }

  deleteEvaluation(evaluation: Evaluation) {
    this._school$.pipe(
      take(1),
      switchMap(school => this._scoreBookService.deleteEvaluation(school.id, evaluation))
    ).subscribe(() => this._store.dispatch(scoreBookEvaluationDeleted({evaluationId: evaluation.id})));
  }

  loadEvaluationScoresForEvaluation(evaluationId: number) {
    this._school$.pipe(
      take(1),
      switchMap(school => this._scoreBookService.loadEvaluationScoresForEvaluation(school.id, evaluationId))
    ).subscribe(scores => this._store.dispatch(scoreBookEvaluationScoresLoaded({scores: scores})));
  }

  saveEvaluationScore(evaluationScore: EvaluationScore) {
    this._school$.pipe(
      take(1),
      switchMap(school => this._scoreBookService.saveEvaluationScore(school.id, evaluationScore))
    ).subscribe(() => this._store.dispatch(scoreBookEvaluationScoreSaved({score: evaluationScore})));
  }

  resetEvaluations() {
    this._store.dispatch(scoreBookEvaluationsReset());
  }

  resetEvaluationScores() {
    this._store.dispatch(scoreBookEvaluationScoresReset());
  }

  loadEvaluationsAverages(attributionId: number, periodCode: string, averageComputationType: AverageComputationType, averageComputationEvaluationType: AverageComputationEvaluationType, includeAllPeriods: boolean) {
    this._school$.pipe(
      take(1),
      switchMap(school => this._scoreBookService.loadEvaluationsAverages(school.id, attributionId, periodCode, averageComputationType, averageComputationEvaluationType, includeAllPeriods))
    ).subscribe(evaluationsAverages => this._store.dispatch(scoreBookEvaluationsAveragesLoaded({evaluationsAverages: evaluationsAverages})));
  }

  saveAveragesToSchoolReport(schoolReportScoresByStudent: {studentId: number; schoolReportScores: SchoolReportScore[]}[]): Observable<any> {
    return this._school$.pipe(
      take(1),
      switchMap(school => {
        const arr = schoolReportScoresByStudent.map(byStudent =>
          byStudent.schoolReportScores.map(score => this._schoolReportsService.saveSchoolReportScore(school.id, school.activeSchoolYear, byStudent.studentId, score))
        );
        return [].concat(...arr); // FlatMap or flat() not supported yet by all browsers...
      }),
      mergeAll(3)
    );
  }

  loadCommentsForEvaluation(evaluationId: number) {
    this._school$.pipe(
      take(1),
      switchMap(school => this._scoreBookService.loadCommentsForEvaluation(school.id, evaluationId))
    ).subscribe(comments => this._store.dispatch(scoreBookEvaluationCommentsLoaded({comments})));
  }

  saveCommentForEvaluation(comment: EvaluationComment) {
    this._school$.pipe(
      take(1),
      switchMap(school => this._scoreBookService.saveCommentForEvaluation(school.id, comment))
    ).subscribe(() => this._store.dispatch(scoreBookEvaluationCommentSaved({comment})));
  }

  resetEvaluationComments() {
    this._store.dispatch(scoreBookEvaluationCommentsReset());
  }

  exportEvaluationsAveragesData(attributionId: number, periodCode: string, averageComputationEvaluationType: AverageComputationEvaluationType, includeAllPeriods: boolean) {
    this._school$.pipe(
      take(1),
      switchMap(
        school => this._scoreBookService.exportEvaluationsAveragesData(school.id, attributionId, periodCode, averageComputationEvaluationType, includeAllPeriods)
      )).subscribe(_file => saveAs(_file.content, _file.filename));
  }

  loadSchootSettings() {
    this._sharedFacade.loadSchoolSettings();
  }
}
